import { Grid, Paper, Typography, Box } from '@mui/material';
import React, { useEffect } from 'react';
import Content from '../components/Layout/Content';
import {
  dateFilterMenuItems,
  VITALS_LABELS,
  VITAL_TYPES,
  VITAL_TYPE_DROPDOWN_ITEMS,
} from '../shared/constants';
import {
  buildSimplifiedVitalsData,
  filterByVitalType,
  handleTimeFilter,
} from '../shared/helper';
import BloodPressureIcon from '../assests/blood_presure_icon.png';
import LungsIcon from '../assests/respirate_icon.png';
import HeartRateIcon from '../assests/heart_rate_icon.png';
import { KeyboardArrowRight } from '@mui/icons-material';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { FETCH_VITALS_QUERY } from '../services/VitalsQueries/VitalsService';
import { useSnackbar } from 'notistack';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import NotFound from '../components/NotFound/NotFoundPage';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import {
  VITALS,
  VITALS_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const rowWrapper = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #E0E0E0',
  p: 2,
  pl: 0,
  cursor: 'pointer',
};

const iconSection = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'left',
};

const boxContainer = {
  mt: 2,
  // position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  flex: 1,
};

const rowIconGetter = key =>
  key === VITAL_TYPES[VITALS.BLOOD_PRESSURE]
    ? {
        icon: BloodPressureIcon,
        alt: VITALS.IMAGE_ALTERNATE_TEXT.BLOOD_PRESSURE,
      }
    : key === VITAL_TYPES[VITALS.HEART_RATE]
    ? { icon: HeartRateIcon, alt: VITALS.IMAGE_ALTERNATE_TEXT.HEART_RATE }
    : key === VITAL_TYPES[VITALS.RESPIRATORY_RATE]
    ? { icon: LungsIcon, alt: VITALS.IMAGE_ALTERNATE_TEXT.LUNGS }
    : key === VITAL_TYPES[VITALS.BODY_TEMPERATURE]
    ? { icon: LungsIcon, alt: VITALS.IMAGE_ALTERNATE_TEXT.BODY_TEMPERATURE }
    : {
        icon: BloodPressureIcon,
        alt: VITALS.IMAGE_ALTERNATE_TEXT.BLOOD_PRESSURE,
      };

const constructVitalUrl = vType => {
  if (vType && VITAL_TYPES[vType]) {
    return `/medical/vitals/${VITAL_TYPES[vType]}`;
  } else {
    return '';
  }
};

const AllVitals = ({ data, onClick }) => {
  if (!data) return <></>;
  return (
    <Paper
      variant="outlined"
      sx={{ width: 343, borderRadius: '12px', pl: 2, pr: 2, pt: 2 }}>
      <Typography variant="titleMobile">{data.date}</Typography>
      <Grid container sx={{ mt: 2 }}>
        {Object.keys(data).map((key, index) => {
          if (key !== 'date') {
            return (
              <Grid item xs={12} key={index}>
                <Box sx={rowWrapper} onClick={() => onClick(key)}>
                  <Box sx={iconSection}>
                    <Box
                      component="img"
                      src={rowIconGetter(key).icon}
                      alt={rowIconGetter(key).alt}
                      sx={{ width: 24, height: 24, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="serviceColumnName">
                        {VITALS_LABELS[key]}
                      </Typography>
                      {data[key].value instanceof Object ? (
                        <Typography variant="h5">
                          {data[key].value.high}/{data[key].value.low}
                          <Typography
                            sx={{ ml: 1 }}
                            display="inline"
                            variant="serviceColumnName">
                            {data[key].unit}
                          </Typography>
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="h5">
                            {data[key].value}{' '}
                            <Typography
                              display="inline"
                              variant="serviceColumnName">
                              {data[key].unit}
                            </Typography>
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <KeyboardArrowRight sx={{ color: '#838383' }} />
                  </Box>
                </Box>
              </Grid>
            );
          }
          return <React.Fragment key={index}></React.Fragment>;
        })}
      </Grid>
    </Paper>
  );
};

export const Vitals = () => {
  const navigate = useNavigate();
  const [formattedData, setFormattedData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [timeFilter, setTimeFilter] = useState(dateFilterMenuItems[0].value);
  const [filteredData, setFilteredData] = useState([]);
  const [vitalTypeFilter, setVitalTypeFilter] = useState(
    VITAL_TYPE_DROPDOWN_ITEMS[0].value
  );

  const { data, loading, error } = useQuery(FETCH_VITALS_QUERY, {
    variables: {
      sourceUrl: '',
      groupBy: 'DATE',
    },
    onError: err => {
      enqueueSnackbar(VITALS.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (data) {
      const vitalsData = buildSimplifiedVitalsData(data.fetchAllVitals);
      setFormattedData(vitalsData);
      setFilteredData(vitalsData);
    }
  }, [data]);

  useEffect(() => {
    const timeFilteredData = handleTimeFilter(timeFilter, formattedData);
    const vitalTypeFilteredData = filterByVitalType(
      vitalTypeFilter,
      timeFilteredData,
      VITAL_TYPE_DROPDOWN_ITEMS
    );
    setFilteredData(vitalTypeFilteredData);
  }, [timeFilter, vitalTypeFilter, formattedData]);

  const vitalFormattedDataFn = vitalType => {
    const temp = [...formattedData];
    const vitalSpecificData = [];
    temp.forEach(bundle => {
      if (bundle[vitalType]) {
        bundle[vitalType].date = bundle.date;
        vitalSpecificData.push(bundle[vitalType]);
      }
    });
    vitalSpecificData.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
    return vitalSpecificData;
  };

  const handleClick = vType => {
    const selectedVital = VITAL_TYPES[vType];
    if (selectedVital) {
      navigate(constructVitalUrl(vType), {
        state: {
          data: vitalFormattedDataFn(vType),
          isBpGraph:
            vType === VITAL_TYPES.BLOOD_PRESSURE ||
            vType === VITAL_TYPES.BLOOD_PRESSURE_CHILDREN
              ? true
              : false,
        },
      });
    } else {
      return;
    }
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      const filterVal = ev.target.value;
      setTimeFilter(filterVal);
    },
  };

  const vitalTypeFilterOptions = {
    options: VITAL_TYPE_DROPDOWN_ITEMS,
    value: vitalTypeFilter,
    changeHandler: ev => {
      const filterVal = ev.target.value;
      setVitalTypeFilter(filterVal);
    },
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{VITALS.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...vitalTypeFilterOptions} filterType="Vitals:" />
      </Box>
      {error || (!loading && !filteredData.length) ? (
        <NotFound title={VITALS.TITLE_TEXT} sx={{ mt: 2 }} />
      ) : (
        <Grid container columnSpacing={1} sx={{ mt: 2 }}>
          {filteredData?.map((section, index) => (
            <Grid item sx={{ mt: 3 }} key={index}>
              <AllVitals data={section} onClick={handleClick} />
            </Grid>
          ))}
        </Grid>
      )}
      <SimpleBackdrop open={loading} />
    </Content>
  );
};
